"use client"

import { createContext, useContext, useEffect, useState, type ReactNode } from "react"
import { tokenManager } from "@/lib/token-manager"
import { env } from "@/lib/env"

type AuthContextType = {
  token: string | null
  setToken: (token: string | null) => void
  isAuthenticated: boolean
  tokenSource: "url" | "parent" | "none"
  requestTokenFromParent: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(null)
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false)
  const [tokenSource, setTokenSource] = useState<"url" | "parent" | "none">("none")
  const [tokenRequested, setTokenRequested] = useState<boolean>(false)

  // Function to request token from parent window
  const requestTokenFromParent = () => {
    // Prevent duplicate requests
    if (tokenRequested) {
      return
    }

    if (typeof window !== "undefined" && window.parent && window.parent !== window) {
      try {
        window.parent.postMessage({
          action: "REQUEST_TOKEN"
        }, "*")
        setTokenRequested(true)
      } catch (error) {
        console.error("Failed to send REQUEST_TOKEN to parent:", error)
      }
    }
  }

  // Wrapper function to update token and notify token manager
  const setToken = (newToken: string | null) => {
    setTokenState(newToken)
    tokenManager.notifyTokenUpdate(newToken)
  }

  // Parse URL parameters on initial load to extract the token
  useEffect(() => {
    const extractTokenFromUrl = () => {
      // Dev/QA convenience only: a token in a URL leaks into history, Referer
      // headers and access logs, so production ignores it and relies solely on
      // the postMessage handshake (appNext routes with queryParams: {}).
      if (typeof window !== "undefined" && env.buildEnv !== "production") {
        const urlParams = new URLSearchParams(window.location.search)
        const tokenFromUrl = urlParams.get("token")

        if (tokenFromUrl) {
          setTokenSource("url")
          setToken(tokenFromUrl)
          return { token: tokenFromUrl, source: "url" as const }
        }

        return { token: null, source: "none" as const }
      }
      return { token: null, source: "none" as const }
    }

    const { token: urlToken } = extractTokenFromUrl()
    setIsAuthenticated(!!urlToken)
    
    // If no token from URL, request from parent (only on initial load)
    if (!urlToken) {
      setTimeout(() => requestTokenFromParent(), 100) // Small delay to ensure parent is ready
    }
  }, []) // Empty dependency array - only run on initial mount

  // Listen for messages from parent window
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (typeof window === "undefined") {
        return
      }

      // Filter on the payload FIRST: every message event on the page reaches
      // this handler (HMR, devtools, third-party scripts), and warning on all
      // of them would bury the one warning that matters.
      if (!event.data || (event.data.action !== "SET_TOKEN" && event.data.action !== "TOKEN_EXPIRED")) {
        return
      }

      // Security: only the Phoenix host may set or clear the token — an
      // unchecked SET_TOKEN would let any embedder inject a bearer token.
      const allowedOrigins = (env.allowedTokenOrigins ?? "")
        .split(",")
        .map((origin) => origin.trim())
        .filter(Boolean)

      // Always allow same origin (standalone/dev usage)
      allowedOrigins.push(window.location.origin)

      // Both the origin AND the sender must check out: same-origin iframes or
      // popups must not be able to set or clear the token either. When the MFE
      // runs standalone, window.parent === window, so same-window messages
      // (dev tooling) still pass.
      if (event.source !== window.parent || !allowedOrigins.includes(event.origin)) {
        console.warn("Ignoring token message from unexpected origin/sender:", event.origin)
        return
      }

      // Process messages from parent window
      try {
        const data = event.data

        // Check if this is a SET_TOKEN message
        if (data && data.action === "SET_TOKEN" && typeof data.token === "string") {
          // Set the token source first, then the token
          setTokenSource("parent")
          setToken(data.token)
          setTokenRequested(false) // Reset flag since we got a token
        } else if (data && data.action === "TOKEN_EXPIRED") {
          // Clear current token and request a new one
          setToken(null)
          setTokenSource("none")
          setIsAuthenticated(false)
          setTokenRequested(false) // Reset flag before requesting new token
          requestTokenFromParent()
        }
      } catch (error) {
        console.error("Error processing message from parent:", error)
      }
    }

    // Add event listener for messages
    if (typeof window !== "undefined") {
      window.addEventListener("message", handleMessage)
    }

    // Clean up event listener
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("message", handleMessage)
      }
    }
  }, [])

  // Update authentication status when token changes
  useEffect(() => {
    setIsAuthenticated(!!token)
  }, [token])

  return (
    <AuthContext.Provider
      value={{
        token,
        setToken,
        isAuthenticated,
        tokenSource,
        requestTokenFromParent,
      }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
} 